import {
	Pencil,
	Video,
	ChevronLeft,
	ChevronDown,
	MessageCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@/components/ui/tooltip";
import { VideoCommentsDrawer } from "../VideoCommentsDrawer";
import { InlineEditableV10 } from "./InlineEditableV10";
import { ValidationCTA } from "./ValidationCTA";
import { DesktopScheduleSection } from "./DesktopScheduleSection";
import type { ContentEvent, PublicationItem } from "./types";
import type { TimelineComment } from "@/types/video-validation";

interface UserProfile {
	first_name?: string | null;
	last_name?: string | null;
	avatar_url?: string | null;
}

interface DesktopInfoPanelProps {
	readonly event: ContentEvent;
	readonly editingField: "title" | "description" | null;
	readonly setEditingField: (f: "title" | "description" | null) => void;
	readonly editTitle: string;
	readonly editDescription: string;
	readonly isReadOnly: boolean;
	readonly isValidated: boolean;
	readonly isValidating: boolean;
	readonly allDatesUnchecked: boolean;
	readonly allItems: PublicationItem[];
	readonly checkedDates: Record<string, boolean>;
	readonly showFeedbackPanel: boolean;
	readonly setShowFeedbackPanel: (v: boolean) => void;
	readonly feedbackComments: TimelineComment[];
	readonly isSendingFeedback: boolean;
	readonly focusCommentId: string | null;
	readonly activeMarkerId: string | null;
	readonly inlineActiveVersion: number;
	readonly inlineCurrentTime: number;
	readonly inlineDuration: number;
	readonly inlinePlaying: boolean;
	readonly userProfile: UserProfile | null;
	readonly isAutoValidated: boolean;
	readonly onSaveTitle: (val: string) => void;
	readonly onSaveDescription: (val: string) => void;
	readonly onValidate: () => void;
	readonly onToggleDate: (key: string) => void;
	readonly onOpenSchedule: () => void;
	readonly onOpenFeedbackPanel: () => void;
	readonly onSeekTo: (time: number) => void;
	readonly onTogglePlay: () => void;
	readonly onSendFeedback: () => void;
	readonly onCommentsChange: (updated: TimelineComment[]) => void;
	readonly onCommentAdded: (id: string) => void;
	readonly setFocusCommentId: (id: string | null) => void;
}

export function DesktopInfoPanel({
	event,
	editingField,
	setEditingField,
	editTitle,
	editDescription,
	isReadOnly,
	isValidated,
	isValidating,
	allDatesUnchecked,
	allItems,
	checkedDates,
	showFeedbackPanel,
	setShowFeedbackPanel,
	feedbackComments,
	isSendingFeedback,
	focusCommentId,
	activeMarkerId,
	inlineActiveVersion,
	inlineCurrentTime,
	inlineDuration,
	inlinePlaying,
	userProfile,
	isAutoValidated,
	onSaveTitle,
	onSaveDescription,
	onValidate,
	onToggleDate,
	onOpenSchedule,
	onOpenFeedbackPanel,
	onSeekTo,
	onTogglePlay,
	onSendFeedback,
	onCommentsChange,
	onCommentAdded,
	setFocusCommentId,
}: DesktopInfoPanelProps) {
	const currentUserName = userProfile
		? `${userProfile.first_name ?? ""} ${userProfile.last_name ?? ""}`.trim() ||
			"Utilisateur"
		: "Utilisateur";

	return (
		<div
			className="flex-shrink-0 flex flex-col rounded-3xl overflow-hidden p-3.5"
			style={{
				width: 318,
				height: 520,
				background: "hsl(var(--v10-bg-surface))",
				border: "1px solid hsl(var(--v10-border))",
			}}
		>
			{showFeedbackPanel ? (
				<>
					{/* Feedback panel header */}
					<div className="p-4 space-y-3 flex-shrink-0 border-b border-white/10">
						<div className="flex items-center justify-between gap-2">
							<button
								onClick={() => setShowFeedbackPanel(false)}
								className="flex items-center gap-1.5 text-sm font-medium transition-colors"
								style={{ color: "hsl(var(--v10-text-secondary))" }}
							>
								<ChevronLeft className="h-4 w-4" />
								Retour
							</button>
							<h2 className="text-sm font-semibold text-white leading-tight flex-1 line-clamp-1 text-right">
								{editTitle || event.title || "Vidéo sans titre"}
							</h2>
						</div>
					</div>
					{/* Embedded VideoCommentsDrawer */}
					<div className="flex-1 overflow-hidden relative">
						<VideoCommentsDrawer
							videoId={event.id}
							videoVersion={inlineActiveVersion + 1}
							currentTime={inlineCurrentTime}
							duration={inlineDuration}
							initialComments={feedbackComments}
							isPlaying={inlinePlaying}
							currentUserName={currentUserName}
							currentUserAvatar={userProfile?.avatar_url ?? undefined}
							initialPosition="full"
							embedded
							onClose={() => setShowFeedbackPanel(false)}
							onSeekTo={onSeekTo}
							onTogglePlay={onTogglePlay}
							onCommentsChange={onCommentsChange}
							onSendFeedback={onSendFeedback}
							isSendingFeedback={isSendingFeedback}
							focusCommentId={focusCommentId}
							onCommentAdded={onCommentAdded}
						/>
					</div>
				</>
			) : (
				<>
					{/* Header — Title + Badge */}
					<div className="flex items-start justify-between gap-2 mb-2">
						{editingField === "title" && !isReadOnly ? (
							<InlineEditableV10
								value={editTitle}
								onSave={onSaveTitle}
								onCancel={() => setEditingField(null)}
								fieldType="title"
							/>
						) : isReadOnly ? (
							<h2 className="text-lg font-semibold text-[hsl(var(--v10-text-primary))] leading-tight flex-1 line-clamp-2 pr-4">
								{editTitle || event.title || "Vidéo sans titre"}
							</h2>
						) : (
							<button
								type="button"
								className="group flex-1 relative cursor-pointer rounded-lg transition-colors hover:bg-white/[0.04] pr-4 text-left"
								onClick={() => setEditingField("title")}
							>
								<h2 className="text-lg font-semibold text-[hsl(var(--v10-text-primary))] leading-tight line-clamp-2">
									{editTitle || event.title || "Vidéo sans titre"}
								</h2>
								<Pencil className="absolute top-0.5 right-0.5 h-3.5 w-3.5 text-[hsl(var(--v10-text-tertiary))] opacity-0 group-hover:opacity-100 transition-opacity" />
							</button>
						)}
						{event.isMultihook && (
							<Badge
								variant="outline"
								className="flex-shrink-0 bg-violet-500/15 border-violet-500/30 text-violet-400 text-[10px] uppercase"
							>
								<Video className="h-3 w-3 mr-1" />
								A/B
							</Badge>
						)}
					</div>

					{/* Description */}
					{editingField === "description" && !isReadOnly ? (
						<InlineEditableV10
							value={editDescription}
							onSave={onSaveDescription}
							onCancel={() => setEditingField(null)}
							fieldType="description"
						/>
					) : (
						(() => {
							const descText = editDescription || event.description;
							if (!descText) return null;
							if (isReadOnly) {
								return (
									<p
										className="text-xs leading-relaxed line-clamp-3 mb-3"
										style={{ color: "hsl(var(--v10-text-secondary))" }}
									>
										{descText}
									</p>
								);
							}
							return (
								<button
									type="button"
									className="group relative cursor-pointer rounded-lg transition-colors hover:bg-white/[0.04] mb-3 text-left w-full"
									onClick={() => setEditingField("description")}
								>
									<p
										className="text-xs leading-relaxed line-clamp-3"
										style={{ color: "hsl(var(--v10-text-secondary))" }}
									>
										{descText}
									</p>
									<Pencil className="absolute top-0.5 right-0.5 h-3 w-3 text-[hsl(var(--v10-text-tertiary))] opacity-0 group-hover:opacity-100 transition-opacity" />
								</button>
							);
						})()
					)}

					<div
						className="h-px my-2"
						style={{ background: "hsl(var(--v10-border))" }}
					/>

					{/* Schedule section — only rendered when there are active (non-disabled) publications */}
					{allItems.length > 0 && (
						<div className="space-y-2 flex-1 overflow-y-auto min-h-0 py-1">
							<p
								className="text-[10px] uppercase tracking-wider font-medium"
								style={{ color: "hsl(var(--v10-text-tertiary))" }}
							>
								Programmation
							</p>
							<DesktopScheduleSection
								allItems={allItems}
								checkedDates={checkedDates}
								isValidated={isValidated}
								isReadOnly={isReadOnly}
								onToggleDate={onToggleDate}
								onOpenSchedule={onOpenSchedule}
							/>
						</div>
					)}

					{/* Bottom actions */}
					<div className="flex flex-col gap-2.5 mt-auto pt-2.5">
						<AnimatePresence>
							{allDatesUnchecked && (
								<motion.div
									initial={{ height: 0, opacity: 0 }}
									animate={{ height: "auto", opacity: 1 }}
									exit={{ height: 0, opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="overflow-hidden"
								>
									<div className="flex items-center justify-center gap-1.5 py-1.5">
										<div
											className="w-1.5 h-1.5 rounded-full"
											style={{ background: "hsl(var(--v10-text-tertiary))" }}
										/>
										<span
											className="text-[11px] font-medium"
											style={{ color: "hsl(var(--v10-text-tertiary))" }}
										>
											Brouillon
										</span>
									</div>
								</motion.div>
							)}
						</AnimatePresence>

						<div className="relative">
							<AnimatePresence mode="wait">
								<ValidationCTA
									isValidated={isValidated}
									allDatesUnchecked={allDatesUnchecked}
									isValidating={isValidating}
									onValidate={onValidate}
								/>
							</AnimatePresence>
						</div>

						{/* Feedback button */}
						{isAutoValidated ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										disabled
										className="w-full flex items-center justify-between px-5 py-3 rounded-xl text-sm font-medium opacity-40 cursor-not-allowed"
										style={{
											border: "1px solid hsl(var(--v10-border))",
											color: "hsl(var(--v10-text-secondary))",
										}}
									>
										<div className="flex items-center gap-2">
											<MessageCircle className="h-4 w-4" />
											<span>Donner un feedback</span>
										</div>
										<ChevronDown className="h-4 w-4 -rotate-90" />
									</button>
								</TooltipTrigger>
								<TooltipContent
									side="top"
									className="max-w-[220px] text-center text-xs"
								>
									Le délai de 15 jours après livraison est dépassé. Les retours
									ne sont plus possibles.
								</TooltipContent>
							</Tooltip>
						) : (
							<button
								onClick={onOpenFeedbackPanel}
								className="w-full flex items-center justify-between px-5 py-3 rounded-xl text-sm font-medium transition-all hover:bg-white/[0.03]"
								style={{
									border: "1px solid hsl(var(--v10-border))",
									color: "hsl(var(--v10-text-secondary))",
								}}
							>
								<div className="flex items-center gap-2">
									<MessageCircle className="h-4 w-4" />
									<span>Donner un feedback</span>
								</div>
								<ChevronDown className="h-4 w-4 -rotate-90" />
							</button>
						)}
					</div>
				</>
			)}
		</div>
	);
}
